import { useEffect, useState } from 'react';
import { useApiClient } from '../hooks/useApiClient';
import type {
  DuplicateMessageStatisticsResponse,
  TopDuplicateMetersResponse,
} from '@metron/shared';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { InfoIcon, Loader2 } from 'lucide-react';
import { toErrorMessage } from '@metron/shared';

// Process names for message counts chart
const MESSAGE_PROCESS_NAMES = {
  dailyBroadcastMessages: { label: 'Daily Broadcast', color: '#3b82f6' },
  getLogMessages: { label: 'Get Log', color: '#8b5cf6' },
  emergencyBroadcastMessages: { label: 'Emergency Broadcast', color: '#f59e0b' },
  waveEmergencyBroadcastMessages: { label: 'Wave Emergency Broadcast', color: '#dc2626' },
  waveIngestMessages: { label: 'Wave Ingest', color: '#10b981' },
  gatewayIngestMessages: { label: 'Gateway Ingest', color: '#ef4444' },
  watersignalFlowMessages: { label: 'Watersignal Flow', color: '#06b6d4' },
};

// Process names for meter counts chart (same colors for consistency)
const METER_PROCESS_NAMES = {
  dailyBroadcastMeters: { label: 'Daily Broadcast', color: '#3b82f6' },
  getLogMeters: { label: 'Get Log', color: '#8b5cf6' },
  emergencyBroadcastMeters: { label: 'Emergency Broadcast', color: '#f59e0b' },
  waveEmergencyBroadcastMeters: { label: 'Wave Emergency Broadcast', color: '#dc2626' },
  waveIngestMeters: { label: 'Wave Ingest', color: '#10b981' },
  gatewayIngestMeters: { label: 'Gateway Ingest', color: '#ef4444' },
  watersignalFlowMeters: { label: 'Watersignal Flow', color: '#06b6d4' },
};

// Types for Recharts tooltip
interface TooltipPayload {
  dataKey: string;
  color: string;
  value: number;
  payload: Record<string, unknown>;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}

// Custom tooltip component for message counts chart
function MessageTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;

  // Format date for display
  const date = new Date(label as string);
  const formattedDate = date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  // Calculate total
  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

  return (
    <div className='bg-background border border-border rounded-lg shadow-lg p-3'>
      <p className='font-semibold mb-2'>{formattedDate}</p>
      <div className='space-y-1'>
        {payload.map((entry: TooltipPayload) => {
          const key = entry.dataKey as keyof typeof MESSAGE_PROCESS_NAMES;
          const processInfo = MESSAGE_PROCESS_NAMES[key];

          return (
            <div key={key} className='flex items-center gap-2'>
              <div
                className='w-3 h-3 rounded-sm'
                style={{ backgroundColor: entry.color }}
              />
              <span className='text-sm'>
                {processInfo?.label || key}: <span className='font-semibold'>{entry.value}</span>
              </span>
            </div>
          );
        })}
        <div className='border-t pt-1 mt-1'>
          <span className='text-sm font-semibold'>Total: {total}</span>
        </div>
      </div>
    </div>
  );
}

// Custom tooltip component for meter counts chart
function MeterTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;

  // Format date for display
  const date = new Date(label as string);
  const formattedDate = date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  // Calculate total
  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

  return (
    <div className='bg-background border border-border rounded-lg shadow-lg p-3'>
      <p className='font-semibold mb-2'>{formattedDate}</p>
      <div className='space-y-1'>
        {payload.map((entry: TooltipPayload) => {
          const key = entry.dataKey as keyof typeof METER_PROCESS_NAMES;
          const processInfo = METER_PROCESS_NAMES[key];

          return (
            <div key={key} className='flex items-center gap-2'>
              <div
                className='w-3 h-3 rounded-sm'
                style={{ backgroundColor: entry.color }}
              />
              <span className='text-sm'>
                {processInfo?.label || key}:{' '}
                <span className='font-semibold'>{entry.value} meters</span>
              </span>
            </div>
          );
        })}
        <div className='border-t pt-1 mt-1'>
          <span className='text-sm font-semibold'>Total: {total} meters</span>
        </div>
      </div>
    </div>
  );
}

// Format process name for display
function formatProcessName(processName: string): string {
  return processName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Format device ID schema for display
function formatDeviceIdSchema(schema: string): string {
  return schema === 'vn_meter' ? 'VN Meter' : 'WaterSignal Meter';
}

export function DuplicateMessagesPage() {
  const apiClient = useApiClient();
  const [data, setData] = useState<DuplicateMessageStatisticsResponse['data']>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Top duplicate meters state
  const [topMeters, setTopMeters] = useState<TopDuplicateMetersResponse['meters']>([]);
  const [topMetersLoading, setTopMetersLoading] = useState(true);
  const [topMetersError, setTopMetersError] = useState<string | null>(null);

  // Fetch duplicate message statistics
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.get<DuplicateMessageStatisticsResponse>(
          'duplicate-messages/statistics',
        );
        setData(response.data);
      } catch (err) {
        setError(
          toErrorMessage(err, 'Failed to fetch duplicate message statistics'),
        );
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [apiClient]);

  // Fetch top duplicate meters
  useEffect(() => {
    async function fetchTopMeters() {
      try {
        setTopMetersLoading(true);
        setTopMetersError(null);
        const response = await apiClient.get<TopDuplicateMetersResponse>(
          'duplicate-messages/top-meters',
        );
        setTopMeters(response.meters);
      } catch (err) {
        setTopMetersError(
          toErrorMessage(err, 'Failed to fetch top duplicate meters'),
        );
      } finally {
        setTopMetersLoading(false);
      }
    }

    fetchTopMeters();
  }, [apiClient]);

  // Format data for charts - convert ISO date to readable format
  const chartData = data.map((point) => {
    const date = new Date(point.day);
    return {
      ...point,
      dayLabel: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    };
  });

  // Calculate totals for the period (message counts)
  const messageTotals = data.reduce(
    (acc, point) => ({
      getLogMessages: acc.getLogMessages + point.getLogMessages,
      dailyBroadcastMessages: acc.dailyBroadcastMessages + point.dailyBroadcastMessages,
      waveIngestMessages: acc.waveIngestMessages + point.waveIngestMessages,
      emergencyBroadcastMessages: acc.emergencyBroadcastMessages +
        point.emergencyBroadcastMessages,
      gatewayIngestMessages: acc.gatewayIngestMessages + point.gatewayIngestMessages,
      watersignalFlowMessages: acc.watersignalFlowMessages + point.watersignalFlowMessages,
    }),
    {
      getLogMessages: 0,
      dailyBroadcastMessages: 0,
      waveIngestMessages: 0,
      emergencyBroadcastMessages: 0,
      gatewayIngestMessages: 0,
      watersignalFlowMessages: 0,
    },
  );

  const totalMessages = Object.values(messageTotals).reduce((sum, val) => sum + val, 0);

  // Calculate totals for the period (meter counts)
  const meterTotals = data.reduce(
    (acc, point) => ({
      getLogMeters: acc.getLogMeters + point.getLogMeters,
      dailyBroadcastMeters: acc.dailyBroadcastMeters + point.dailyBroadcastMeters,
      waveIngestMeters: acc.waveIngestMeters + point.waveIngestMeters,
      emergencyBroadcastMeters: acc.emergencyBroadcastMeters + point.emergencyBroadcastMeters,
      gatewayIngestMeters: acc.gatewayIngestMeters + point.gatewayIngestMeters,
      watersignalFlowMeters: acc.watersignalFlowMeters + point.watersignalFlowMeters,
    }),
    {
      getLogMeters: 0,
      dailyBroadcastMeters: 0,
      waveIngestMeters: 0,
      emergencyBroadcastMeters: 0,
      gatewayIngestMeters: 0,
      watersignalFlowMeters: 0,
    },
  );

  const totalMeters = Object.values(meterTotals).reduce((sum, val) => sum + val, 0);

  // Render message chart content based on loading/error state
  const renderMessageChartContent = () => {
    if (loading) {
      return (
        <div className='h-[400px] flex items-center justify-center'>
          <div className='flex flex-col items-center gap-2'>
            <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            <p className='text-muted-foreground'>Loading chart data...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className='h-[400px] flex items-center justify-center'>
          <p className='text-red-500'>Error: {error}</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width='100%' height={400}>
        <BarChart data={chartData}>
          <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
          <XAxis dataKey='dayLabel' tick={{ fontSize: 11 }} />
          <YAxis />
          <Tooltip content={<MessageTooltip />} />
          <Legend />
          {Object.entries(MESSAGE_PROCESS_NAMES).map(([key, { label, color }]) => (
            <Bar
              key={key}
              type='monotone'
              dataKey={key}
              stackId='1'
              fill={color}
              name={label}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // Render meter chart content based on loading/error state
  const renderMeterChartContent = () => {
    if (loading) {
      return (
        <div className='h-[400px] flex items-center justify-center'>
          <div className='flex flex-col items-center gap-2'>
            <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            <p className='text-muted-foreground'>Loading chart data...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className='h-[400px] flex items-center justify-center'>
          <p className='text-red-500'>Error: {error}</p>
        </div>
      );
    }

    return (
      <ResponsiveContainer width='100%' height={400}>
        <BarChart data={chartData}>
          <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
          <XAxis dataKey='dayLabel' tick={{ fontSize: 11 }} />
          <YAxis />
          <Tooltip content={<MeterTooltip />} />
          <Legend />
          {Object.entries(METER_PROCESS_NAMES).map(([key, { label, color }]) => (
            <Bar
              key={key}
              type='monotone'
              dataKey={key}
              stackId='1'
              fill={color}
              name={label}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // Render top meters table content
  const renderTopMetersTable = () => {
    if (topMetersLoading) {
      return (
        <div className='h-[200px] flex items-center justify-center'>
          <div className='flex flex-col items-center gap-2'>
            <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
            <p className='text-muted-foreground'>Loading top meters data...</p>
          </div>
        </div>
      );
    }

    if (topMetersError) {
      return (
        <div className='h-[200px] flex items-center justify-center'>
          <p className='text-red-500'>Error: {topMetersError}</p>
        </div>
      );
    }

    if (topMeters.length === 0) {
      return (
        <div className='h-[200px] flex items-center justify-center'>
          <p className='text-muted-foreground'>No duplicate meters found in the last 24 hours.</p>
        </div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Device ID Schema</TableHead>
            <TableHead>Meter ID</TableHead>
            <TableHead>Process Name</TableHead>
            <TableHead className='text-right'>Message Count</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {topMeters.map((meter, index) => (
            <TableRow key={`${meter.sensorId}-${meter.processName}-${index}`}>
              <TableCell>{formatDeviceIdSchema(meter.deviceIdSchema)}</TableCell>
              <TableCell className='font-mono'>{meter.deviceId}</TableCell>
              <TableCell>{formatProcessName(meter.processName)}</TableCell>
              <TableCell className='text-right font-semibold'>
                {meter.messageCount.toLocaleString()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className='container mx-auto px-4 py-8 space-y-6'>
      <div className='flex items-center justify-between'>
        <h1 className='text-3xl font-bold'>Duplicate Message Processing</h1>
        <p className='text-muted-foreground'>Last 14 days</p>
      </div>

      {/* Explanation Alert */}
      <Alert>
        <InfoIcon className='h-4 w-4' />
        <AlertDescription>
          <strong>What is a duplicate message?</strong>{' '}
          A duplicate occurs when the same meter is processed more than twice in a single day by the
          same process type. This chart shows the count of extra messages beyond two per day. For
          example, if a meter is processed 5 times by daily_broadcast on one day, that contributes 3
          to the duplicate count (5 - 2 = 3).
        </AlertDescription>
      </Alert>

      {/* Stacked Bar Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Total duplicate messages by process</CardTitle>
          <CardDescription>
            Stacked view of duplicate sensor messages by process type
            {!loading && !error && totalMessages > 0 && (
              <>
                {' • '}Total: {totalMessages.toLocaleString()} duplicate messages over 14 days
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderMessageChartContent()}
        </CardContent>
      </Card>

      {/* Meter Counts Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Total meters with duplicate messages by process</CardTitle>
          <CardDescription>
            Number of unique meters with duplicate processing by process type
            {!loading && !error && totalMeters > 0 && (
              <>
                {' • '}Total: {totalMeters.toLocaleString()} meter-days with duplicates over 14 days
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderMeterChartContent()}
        </CardContent>
      </Card>

      {/* Top Duplicate Meters Table */}
      <Card>
        <CardHeader>
          <CardTitle>Top 20 meters by message count (last 24 hours)</CardTitle>
          <CardDescription>
            Meters with the highest number of messages processed in the last 24 hours
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderTopMetersTable()}
        </CardContent>
      </Card>
    </div>
  );
}
